// FILE: PullRequestsView.tsx
// Purpose: The Pull Requests section — filters, the list itself, and the loading/empty/error
//          states around it.
// Layer: Route-level screen
// Exports: PullRequestsView
//
// ── What the list is reading ──────────────────────────────────────────────────────────────────
// One request answers for every project the reader may see at once, so the section is a page per
// read and not a page per repository. Three consequences shape this file:
//
//   - A project that could not be read comes back in `errors[]` while the healthy ones still
//     return their rows. The banner names it; the list stays.
//   - A project the reader has no `p.pull_requests.view` on appears in neither `entries` nor
//     `errors`. It
//     is not "hidden", it is not part of the answer — so nothing here may hint that it exists.
//   - "Load more" is page + 1 asked of every project at once. Later pages are held as their own
//     queries so that only the first one polls: re-asking five pages on a timer would multiply
//     the token's rate budget by five for rows nobody is looking at.

import type {
  ProjectId,
  PullRequestListEntry,
  PullRequestListInput,
  PullRequestProjectError,
} from "@taskin-code/contracts";
import { PULL_REQUEST_LIST_DEFAULT_PER_PAGE } from "@taskin-code/contracts";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuthStore } from "~/auth/authStore";
import { useStore } from "~/store";
import {
  pullRequestsInvalidateMutationOptions,
  pullRequestsListQueryOptions,
} from "~/lib/pullRequestsReactQuery";
import {
  DEFAULT_PULL_REQUEST_LIST_STATE,
  PULL_REQUEST_LIST_STATES,
  type PullRequestsRouteSearch,
} from "~/lib/pullRequestsRouteSearch";
import { GitPullRequestIcon, RefreshCwIcon } from "~/lib/icons";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { SidebarHeaderTrigger, SidebarInset } from "~/components/ui/sidebar";
import { cn } from "~/lib/utils";
import { PullRequestDetailPanel } from "./PullRequestDetailPanel";
import { PullRequestFailure } from "./PullRequestFailure";
import { PullRequestRow, PullRequestRowSkeleton } from "./PullRequestRow";
import { isViewerAuthored } from "./pullRequestPresentation";

const ROUTE_ID = "/_chat/pull-requests" as const;
const ALL_VALUE = "__all__";
const SKELETON_ROW_KEYS = ["1", "2", "3", "4", "5"] as const;

const STATE_LABELS: Record<(typeof PULL_REQUEST_LIST_STATES)[number], string> = {
  open: "Open",
  merged: "Merged",
  closed: "Closed",
  all: "All states",
};

/** Stable identity of a row: two projects can hold the same pull request number. */
const entryKey = (entry: PullRequestListEntry): string => `${entry.projectId}#${entry.number}`;

export function PullRequestsView() {
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projects = useStore((store) => store.projects);
  const projectAssignments = useAuthStore((state) => state.projectAssignments);

  // How many pages the reader has asked for. Not a URL param: it is an accumulation, not a
  // filter — see `pullRequestsRouteSearch.ts`.
  const [pageCount, setPageCount] = useState(1);
  // Every author seen since the filters last changed. Kept outside the current answer because
  // narrowing by author makes the server return that author alone: derived straight from the
  // rows, the picker would collapse to the person already picked and there would be no way back
  // to anybody else.
  const [knownAuthors, setKnownAuthors] = useState<ReadonlyArray<string>>([]);
  // The open pull request, held as the row itself: the panel draws its header from what the list
  // already knows, so opening one paints a title and a state glyph before the detail call lands.
  // Not a URL param — the panel is a place in a session, and a link to it would make the
  // recipient's first read a detail, an activity and possibly a patch they never asked for.
  const [selected, setSelected] = useState<PullRequestListEntry | null>(null);

  const { projectId, author } = search;
  // The URL omits the default rather than spelling it out, so `/pull-requests` on its own is the
  // open list — which is what the sidebar entry links to.
  const state = search.state ?? DEFAULT_PULL_REQUEST_LIST_STATE;

  // A different question is a different list: page two of the old filters says nothing about the
  // new ones, and neither does the set of authors that answered them.
  useEffect(() => {
    setPageCount(1);
    setKnownAuthors((previous) => (previous.length === 0 ? previous : []));
  }, [state, projectId, author]);

  const listInputs = useMemo<ReadonlyArray<PullRequestListInput>>(
    () =>
      Array.from({ length: pageCount }, (_unused, index) => ({
        state,
        ...(projectId ? { projectId } : {}),
        ...(author ? { filters: { author } } : {}),
        page: index + 1,
        perPage: PULL_REQUEST_LIST_DEFAULT_PER_PAGE,
      })),
    [author, pageCount, projectId, state],
  );

  const pageQueries = useQueries({
    queries: listInputs.map((input, index) => {
      const options = pullRequestsListQueryOptions(input);
      // Only the first page polls. The rest are refreshed by focus, by reconnect, and by the
      // refresh button — none of which fire on a timer nobody asked for. Mutated rather than
      // spread because the builder just handed us a fresh object nobody else holds.
      if (index > 0) options.refetchInterval = false;
      return options;
    }),
  });

  const firstPage = pageQueries[0];
  const lastPage = pageQueries[pageQueries.length - 1];
  const isInitialLoading = firstPage?.isLoading ?? true;
  const isInitialError = firstPage?.isError ?? false;

  const { entries, errors, viewers } = useMemo(() => {
    const byKey = new Map<string, PullRequestListEntry>();
    const errorByProject = new Map<string, PullRequestProjectError>();
    const mergedViewers: Record<string, string> = {};

    for (const query of pageQueries) {
      const data = query.data;
      if (!data) continue;
      for (const entry of data.entries) {
        // First page wins: a pull request updated between two reads can land on both.
        if (!byKey.has(entryKey(entry))) byKey.set(entryKey(entry), entry);
      }
      for (const error of data.errors) {
        if (!errorByProject.has(error.projectId)) errorByProject.set(error.projectId, error);
      }
      Object.assign(mergedViewers, data.viewers);
    }

    return {
      // Sorted across pages, not just within one: each project pages independently, so page two
      // of a busy repository can be newer than page one of a quiet one.
      entries: [...byKey.values()].toSorted((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
      errors: [...errorByProject.values()],
      viewers: mergedViewers,
    };
  }, [pageQueries]);

  // Accumulate the authors on offer. Guarded against re-setting an identical list, which would
  // otherwise loop: the effect depends on the rows it reads.
  useEffect(() => {
    setKnownAuthors((previous) => {
      const next = new Set(previous);
      for (const entry of entries) {
        if (entry.author) next.add(entry.author.login);
      }
      for (const viewer of Object.values(viewers)) next.add(viewer);
      if (next.size === previous.length) return previous;
      return [...next].toSorted((a, b) => a.localeCompare(b));
    });
  }, [entries, viewers]);

  /**
   * Which projects the picker may name. The reader's own `p.pull_requests.view` grants when there
   * are any;
   * otherwise whatever the server just answered for — never the whole project list, which would
   * put repositories the reader cannot open in front of them.
   */
  const projectOptions = useMemo(() => {
    const granted = projects.filter(
      (project) => projectAssignments.get(project.id)?.has("p.pull_requests.view") ?? false,
    );
    if (granted.length > 0) {
      return granted.map((project) => ({ id: project.id, title: project.name }));
    }
    const answered = new Map<string, string>();
    for (const entry of entries) answered.set(entry.projectId, entry.projectTitle);
    for (const error of errors) answered.set(error.projectId, error.projectTitle);
    return [...answered].map(([id, title]) => ({ id: id as ProjectId, title }));
  }, [entries, errors, projectAssignments, projects]);

  // Written whole rather than patched onto whatever was there: the search object is this route's
  // entire URL state, and `undefined` in the patch is how a filter is cleared.
  const updateSearch = useCallback(
    (patch: PullRequestsRouteSearch) => {
      const next: PullRequestsRouteSearch = { ...search, ...patch };
      void navigate({
        to: "/pull-requests",
        replace: true,
        search: {
          ...(next.state && next.state !== DEFAULT_PULL_REQUEST_LIST_STATE
            ? { state: next.state }
            : {}),
          ...(next.projectId ? { projectId: next.projectId } : {}),
          ...(next.author ? { author: next.author } : {}),
        },
      });
    },
    [navigate, search],
  );

  const invalidate = useMutation(pullRequestsInvalidateMutationOptions({ queryClient }));
  const isRefreshing = invalidate.isPending || pageQueries.some((query) => query.isFetching);

  const openDetail = useCallback((entry: PullRequestListEntry) => {
    setSelected(entry);
  }, []);
  const closeDetail = useCallback(() => setSelected(null), []);

  const authored = entries.filter((entry) => isViewerAuthored(entry, viewers));
  const others = entries.filter((entry) => !isViewerAuthored(entry, viewers));
  // Nothing to separate when the reader wrote none of it, or when no project could tell us who
  // the reader is — an "Everyone else" heading over the whole list says nothing.
  const isGrouped = authored.length > 0 && others.length > 0;
  const selectedKey = selected === null ? null : entryKey(selected);
  const hasMore = lastPage?.data?.hasMore ?? false;
  const isLoadingMore = lastPage?.isLoading ?? false;

  return (
    <SidebarInset className="isolate h-dvh min-h-0 overflow-hidden bg-background">
      <div className="flex h-full flex-col">
        {/* ── Top nav ───────────────────────────────────────────────────── */}
        <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4 sm:px-6">
          <SidebarHeaderTrigger className="size-7 shrink-0" />
          <div className="flex items-center gap-2">
            <GitPullRequestIcon className="size-4 text-muted-foreground/70" />
            <h1 className="text-sm font-semibold text-foreground">Pull Requests</h1>
          </div>
          <div className="ms-auto flex items-center gap-2">
            <Button
              size="xs"
              variant="ghost"
              disabled={isRefreshing}
              onClick={() => invalidate.mutate({})}
              aria-label="Refresh pull requests"
            >
              <RefreshCwIcon className={isRefreshing ? "size-3.5 animate-spin" : "size-3.5"} />
              Refresh
            </Button>
          </div>
        </div>

        {/* ── Filters ───────────────────────────────────────────────────── */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2 sm:px-5">
          <Select
            value={state}
            onValueChange={(value) => {
              if (typeof value !== "string") return;
              updateSearch({ state: value as PullRequestsRouteSearch["state"] });
            }}
          >
            <SelectTrigger size="xs" className="w-32" aria-label="Filter by state">
              <SelectValue>{STATE_LABELS[state]}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              {PULL_REQUEST_LIST_STATES.map((option) => (
                <SelectItem key={option} hideIndicator value={option}>
                  {STATE_LABELS[option]}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>

          <Select
            value={projectId ?? ALL_VALUE}
            onValueChange={(value) => {
              if (typeof value !== "string") return;
              updateSearch({
                projectId: value === ALL_VALUE ? undefined : (value as ProjectId),
              });
            }}
          >
            <SelectTrigger size="xs" className="w-44" aria-label="Filter by project">
              <SelectValue>
                {projectId
                  ? (projectOptions.find((option) => option.id === projectId)?.title ?? projectId)
                  : "All projects"}
              </SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              <SelectItem hideIndicator value={ALL_VALUE}>
                All projects
              </SelectItem>
              {projectOptions.map((option) => (
                <SelectItem key={option.id} hideIndicator value={option.id}>
                  {option.title}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>

          <Select
            value={author ?? ALL_VALUE}
            onValueChange={(value) => {
              if (typeof value !== "string") return;
              updateSearch({ author: value === ALL_VALUE ? undefined : value });
            }}
          >
            <SelectTrigger size="xs" className="w-40" aria-label="Filter by author">
              <SelectValue>{author ?? "All authors"}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              <SelectItem hideIndicator value={ALL_VALUE}>
                All authors
              </SelectItem>
              {/* The active filter stays on offer even when it is nobody the current answer
                  mentions — otherwise a link arriving with an author would show a blank picker. */}
              {(author && !knownAuthors.includes(author)
                ? [author, ...knownAuthors]
                : knownAuthors
              ).map((login) => (
                <SelectItem key={login} hideIndicator value={login}>
                  {login}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        </div>

        {/* ── List, and the pull request opened beside it ───────────────── */}
        <div className="flex min-h-0 flex-1">
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5",
              // Narrow screens have room for one of the two. The panel wins while it is open,
              // and closing it brings the list straight back.
              selected ? "max-lg:hidden" : "",
            )}
          >
            {isInitialLoading ? (
              <div className="space-y-1.5">
                {SKELETON_ROW_KEYS.map((key) => (
                  <PullRequestRowSkeleton key={key} />
                ))}
              </div>
            ) : isInitialError ? (
              <ErrorPanel
                title="Could not load pull requests"
                description={
                  firstPage?.error instanceof Error
                    ? firstPage.error.message
                    : "Something went wrong talking to the server."
                }
                onRetry={() => void firstPage?.refetch()}
              />
            ) : entries.length === 0 && errors.length === 0 ? (
              <EmptyPanel
                title={state === "open" ? "No open pull requests" : "Nothing matches these filters"}
                description={
                  state === "open" && !projectId && !author
                    ? "Every repository this account can read is caught up."
                    : "Try widening the state, project or author filter."
                }
              />
            ) : (
              <div className="space-y-3">
                {errors.map((error) => (
                  <PullRequestFailure
                    key={error.projectId}
                    error={new Error(error.message)}
                    reason={error.reason}
                    context={error.projectTitle}
                    fallbackTitle="Could not be read"
                  />
                ))}

                {isGrouped ? (
                  <>
                    <RowGroup
                      title="Yours"
                      entries={authored}
                      selectedKey={selectedKey}
                      onSelect={openDetail}
                    />
                    <RowGroup
                      title="Everyone else"
                      entries={others}
                      selectedKey={selectedKey}
                      onSelect={openDetail}
                    />
                  </>
                ) : (
                  <div className="space-y-1.5">
                    {entries.map((entry) => (
                      <PullRequestRow
                        key={entryKey(entry)}
                        entry={entry}
                        selected={selected !== null && entryKey(selected) === entryKey(entry)}
                        onSelect={openDetail}
                      />
                    ))}
                  </div>
                )}

                {hasMore ? (
                  <div className="flex justify-center pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isLoadingMore}
                      onClick={() => setPageCount((previous) => previous + 1)}
                    >
                      {isLoadingMore ? "Loading…" : "Load more"}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {selected ? <PullRequestDetailPanel entry={selected} onClose={closeDetail} /> : null}
        </div>
      </div>
    </SidebarInset>
  );
}

function RowGroup({
  title,
  entries,
  selectedKey,
  onSelect,
}: {
  title: string;
  entries: ReadonlyArray<PullRequestListEntry>;
  selectedKey: string | null;
  onSelect: (entry: PullRequestListEntry) => void;
}) {
  return (
    <section className="space-y-1.5">
      <h2 className="px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {title}
        <span className="ms-1.5 tabular-nums opacity-70">{entries.length}</span>
      </h2>
      {entries.map((entry) => (
        <PullRequestRow
          key={entryKey(entry)}
          entry={entry}
          selected={selectedKey === entryKey(entry)}
          onSelect={onSelect}
        />
      ))}
    </section>
  );
}

function EmptyPanel({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex min-h-40 items-center justify-center rounded-xl border border-dashed border-border/60 bg-background/40 px-5 py-6 text-center">
      <div className="max-w-sm space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function ErrorPanel({
  title,
  description,
  onRetry,
}: {
  title: string;
  description: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-destructive/40 bg-destructive/5 px-5 py-6 text-center">
      <div className="max-w-sm space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Button size="xs" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}
